import { useState, useEffect } from 'react';
import { Link, useLocation } from "react-router-dom";
import Config from "../../Config";
import { useLanguage, SUPPORTED_LANGUAGES } from "../../i18n";

function Navbar() {
    const location = useLocation();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [langMenuOpen, setLangMenuOpen] = useState(false);
    const { language, setLanguage, t } = useLanguage();

    const isBlogPage = location.pathname.startsWith('/blog');
    const isResumePage = location.pathname === '/resume';

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close lang dropdown when clicking outside
    useEffect(() => {
        const handleClick = (e) => {
            if (!e.target.closest('#lang-switcher')) setLangMenuOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const navLinks = [
        { path: '/', label: t('nav.home'), icon: '🏠' },
        { path: '/blog', label: t('nav.blog'), icon: '✍️' },
        { path: '/resume', label: t('nav.resume'), icon: '📄' },
    ];

    const currentLang = SUPPORTED_LANGUAGES.find(l => l.code === language);

    return (
        <>
            <nav
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                    scrolled || isBlogPage || isResumePage
                        ? 'bg-white/90 backdrop-blur-md shadow-sm py-3'
                        : 'bg-transparent py-5'
                }`}
            >
                <div className="max-w-6xl mx-auto px-5 flex items-center justify-between">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-3 group">
                        <div
                            className={`rounded-full bg-gradient-to-br from-gray-900 to-gray-700 flex items-center justify-center text-white font-bold transition-all duration-500 ${
                                isBlogPage || isResumePage ? 'w-10 h-10 text-sm' : 'w-12 h-12 text-base'
                            }`}
                        >
                            {Config.initials}
                        </div>
                        <div className={`hidden sm:block transition-all duration-300 ${
                            scrolled || isBlogPage || isResumePage
                                ? 'opacity-100 translate-x-0'
                                : 'opacity-0 -translate-x-4'
                        }`}>
                            <span className="font-semibold text-gray-900">{Config.firstName}</span>
                            {isBlogPage && <span className="text-gray-500 ml-2">/ {t('nav.blog')}</span>}
                            {isResumePage && <span className="text-gray-500 ml-2">/ {t('nav.resume')}</span>}
                        </div>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center gap-1">
                        {navLinks.map((link) => (
                            <Link
                                key={link.path}
                                to={link.path}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                    location.pathname === link.path ||
                                    (link.path !== '/' && location.pathname.startsWith(link.path))
                                        ? 'bg-gray-100 text-gray-900'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                }`}
                            >
                                {link.label}
                            </Link>
                        ))}

                        {/* Language Switcher — Desktop */}
                        <div id="lang-switcher" className="relative ml-2">
                            <button
                                onClick={() => setLangMenuOpen(!langMenuOpen)}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-all duration-200"
                            >
                                <span>{currentLang?.flag}</span>
                                <span>{currentLang?.label}</span>
                                <svg className={`w-3 h-3 transition-transform ${langMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            {langMenuOpen && (
                                <div className="absolute right-0 mt-1 bg-white border border-gray-100 rounded-xl shadow-lg overflow-hidden z-50 min-w-[110px]">
                                    {SUPPORTED_LANGUAGES.map((lang) => (
                                        <button
                                            key={lang.code}
                                            onClick={() => { setLanguage(lang.code); setLangMenuOpen(false); }}
                                            className={`flex items-center gap-2 w-full px-4 py-2.5 text-sm transition-colors ${
                                                language === lang.code
                                                    ? 'bg-gray-100 text-gray-900 font-semibold'
                                                    : 'text-gray-600 hover:bg-gray-50'
                                            }`}
                                        >
                                            <span>{lang.flag}</span>
                                            <span>{lang.label}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-100"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {mobileMenuOpen ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            )}
                        </svg>
                    </button>
                </div>

                {/* Mobile Menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden absolute top-full left-0 right-0 bg-white border-b border-gray-200 shadow-lg">
                        <div className="px-5 py-4 space-y-2">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                                        location.pathname === link.path ||
                                        (link.path !== '/' && location.pathname.startsWith(link.path))
                                            ? 'bg-gray-100 text-gray-900'
                                            : 'text-gray-600 hover:bg-gray-50'
                                    }`}
                                >
                                    <span>{link.icon}</span>
                                    {link.label}
                                </Link>
                            ))}

                            {/* Language Switcher — Mobile */}
                            <div className="border-t border-gray-100 pt-3 mt-2">
                                <p className="text-xs text-gray-400 px-4 mb-2 uppercase tracking-wide">Language</p>
                                <div className="flex gap-2 px-4">
                                    {SUPPORTED_LANGUAGES.map((lang) => (
                                        <button
                                            key={lang.code}
                                            onClick={() => { setLanguage(lang.code); setMobileMenuOpen(false); }}
                                            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                                language === lang.code
                                                    ? 'bg-gray-900 text-white'
                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                        >
                                            <span>{lang.flag}</span>
                                            <span>{lang.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </nav>
        </>
    );
}

export default Navbar;
